// The in-memory API's operations routes (add-ons, failed events, the operator DE list, the cost
// ledger, report resend and reconcile), with the API's rules: entitlement changes take effect
// now (no future start, no end date), an add-on that is not sold yet cannot be enabled, a
// dependency cannot be switched off under an add-on that needs it; a failed consumer record is
// retried or skipped once; manual costs dedupe on their meter identity and are reversed once;
// a sent report resends under a fresh Idempotency-Key; only an unconfirmed delivery reconciles.
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { ADDON_CATALOG, type AddonKey } from "../features/addons/catalog";
import {
  type Ctx,
  type Handler,
  json,
  notFound,
  type Principal,
  page,
  problem,
  validation,
} from "./fake";
import type { AgencyFacts } from "./fake-agency";
import {
  COMPANY_IDS,
  costLedger,
  deSummary,
  type FakeCompany,
  type FakeDe,
  uid,
} from "./fake-data";
import type {
  ConsumerRecord,
  CostEvent,
  EntitlementStatus,
  FridayReportRecord,
  MeResponse,
  Scorecard,
} from "./types";

type Entitlements = OperationOutput<"operator.entitlements.get">;
type LiveRow = Entitlements["live"][number];
type CacheRow = Entitlements["cache"][number];

export type OpsState = {
  consumers: ConsumerRecord[];
  /** Entitlement rows per company (history; the live one has `ends_on` null). */
  entitlementRows: Map<string, LiveRow[]>;
  cache: Map<string, CacheRow[]>;
  costs: CostEvent[];
  /** Resend operations per report id (`resend:{key}`), oldest first. */
  resends: Map<string, string[]>;
  /** Delivery ledger overrides: `${reportId}|${email}|${operation}` → state. */
  deliveries: Map<string, "unconfirmed" | "sent" | "failed">;
};

const GRACE_DAYS = 90;
/** Where the in-memory build's AiGentPanel links point: a host that never resolves. */
export const FAKE_PANEL_ORIGIN = "https://aigentpanel.invalid";
const ON = new Set<EntitlementStatus>(["active", "trial"]);

export function createOpsState(now: () => Date): OpsState {
  const at = new Date(now().getTime() - 5 * 3_600_000).toISOString();
  return {
    consumers: [
      {
        consumer_name: "proj.kpi",
        company_id: COMPANY_IDS.acme,
        event_id: uid(0x9000, 1),
        event_seq: 4182,
        state: "failed",
        attempts: 5,
        last_error: "observation for de.approval_rate: formula input missing",
        processed_at: at,
        next_attempt_at: null,
      },
    ],
    entitlementRows: new Map(),
    cache: new Map(),
    costs: [],
    resends: new Map(),
    deliveries: new Map(),
  };
}

/**
 * WF-CONTROL: an owner's (or the operator's) switch of an add-on from Setup, as a ledger row, when
 * the company's rows were read already (else they are seeded from `/v1/me` on the first read).
 */
export function recordSetupSwitch(
  state: OpsState,
  companyId: string,
  key: AddonKey,
  status: EntitlementStatus,
  userId: string,
  note: string,
  now: Date,
): void {
  const rows = state.entitlementRows.get(companyId);
  if (!rows) return;
  const today = now.toISOString().slice(0, 10);
  for (const r of rows) if (r.feature_key === key && r.ends_on === null) r.ends_on = today;
  rows.push({
    id: uid(0xe200, rows.length + 1 + (Number.parseInt(companyId.slice(-4), 16) % 4096) * 64),
    company_id: companyId,
    feature_key: key,
    status,
    source: "addon",
    bundled_by_feature_key: null,
    starts_on: today,
    ends_on: null,
    grace_until: null,
    granted_by_user_id: userId,
    note,
    created_at: now.toISOString(),
  });
}

type Deps = {
  now: () => Date;
  todayWeek: () => string;
  /** Every account's `/v1/me` (mutated in place: an entitlement change shows on the next read). */
  accounts: () => MeResponse[];
  companies: () => FakeCompany[];
  desOf: (companyId: string) => FakeDe[];
  principalFor: (me: MeResponse, companyId: string | undefined) => Principal | null;
  kpiOn: (me: MeResponse, companyId: string) => boolean;
  cardFor: (p: Principal, de: FakeDe, week: string) => Scorecard;
  reportsOf: (company: FakeCompany) => FridayReportRecord[];
  sendBase: Handler;
  /** M5-05: the agency console's week of a DE (supervisor, cost, supervision, approvals). */
  deWeek: AgencyFacts["deWeek"];
};

const addDays = (iso: string, days: number) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;
const METER = /^[a-z][a-z0-9_]*:[^:\s]+:\S+$/;
const RESERVED = ["sub:", "time_entry:", "reversal:", "litellm:"];
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function createOpsHandlers(state: OpsState, deps: Deps): Partial<Record<RouteId, Handler>> {
  const today = () => deps.now().toISOString().slice(0, 10);

  /** The operator's grant on a company (operator level), else null: 404 to anyone else. */
  const operatorOf = (me: MeResponse, companyId: string | undefined) =>
    me.agency?.grants.find((g) => g.company_id === companyId && g.level === "operator") ?? null;

  /** The company's rows, seeded from the `/v1/me` map the first time (one row per add-on on). */
  function rowsOf(companyId: string): LiveRow[] {
    let rows = state.entitlementRows.get(companyId);
    if (!rows) {
      const map = deps.accounts().find((m) => m.entitlements[companyId])?.entitlements[companyId];
      const created = new Date(deps.now().getTime() - 30 * 86_400_000).toISOString();
      rows = Object.entries(map ?? {})
        .filter(([k]) => k !== "core")
        .map(([k, status], i) => ({
          id: uid(0xe000, i + 1 + (Number.parseInt(companyId.slice(-4), 16) % 4096) * 16),
          company_id: companyId,
          feature_key: k as AddonKey,
          status: status as EntitlementStatus,
          source: "addon" as const,
          bundled_by_feature_key: null,
          starts_on: created.slice(0, 10),
          ends_on: null,
          grace_until: status === "grace" ? addDays(created.slice(0, 10), GRACE_DAYS) : null,
          granted_by_user_id: null,
          note: null,
          created_at: created,
        }));
      state.entitlementRows.set(companyId, rows);
    }
    return rows;
  }

  const live = (companyId: string) => rowsOf(companyId).filter((r) => r.ends_on === null);
  const statusOf = (companyId: string, key: string): EntitlementStatus =>
    live(companyId).find((r) => r.feature_key === key)?.status ?? "disabled";

  function view(companyId: string): Entitlements {
    const rows = live(companyId);
    return {
      company_id: companyId,
      live: rows,
      cache: rows.map((r) => ({
        company_id: companyId,
        feature_key: r.feature_key,
        status: r.status,
        read_only_until: r.grace_until,
        synced_at: r.created_at,
      })),
    };
  }

  function setStatus(companyId: string, key: AddonKey, status: EntitlementStatus, row: LiveRow) {
    const rows = rowsOf(companyId);
    for (const r of rows)
      if (r.feature_key === key && r.ends_on === null) r.ends_on = row.starts_on;
    rows.push(row);
    // Every account that sees the company reads the new status on its next `/v1/me`.
    for (const me of deps.accounts()) {
      const map = me.entitlements[companyId];
      if (map) map[key] = status;
    }
  }

  const reportById = (p: Principal, id: string | undefined) =>
    deps.reportsOf(p.company).find((r) => r.id === id) ?? null;

  /** The company's ledger as the caller may read it: generated rows plus posted ones. */
  function ledgerOf(p: Principal): CostEvent[] {
    return [
      ...deps.desOf(p.company.id).flatMap((de) => costLedger(de, deps.todayWeek())),
      ...state.costs.filter((c) => c.company_id === p.company.id),
    ].filter((e) => e.visibility === "client" || p.operator);
  }

  const reversed = (id: string) =>
    state.costs.some((c) => c.meter_identity === `reversal:cost_event:${id}`);

  let costSeq = 0;
  let launchSeq = 0;
  function post(
    p: Principal,
    row: Omit<CostEvent, "id" | "company_id" | "period_key" | "created_at">,
  ) {
    const at = deps.now().toISOString();
    const event: CostEvent = {
      ...row,
      id: uid(0x5f00, ++costSeq),
      company_id: p.company.id,
      period_key: row.occurred_at.slice(0, 7),
      created_at: at,
    };
    state.costs.push(event);
    return event;
  }

  return {
    "operator.entitlements.get": ({ me, params }) => {
      if (!operatorOf(me, params.company_id)) return notFound();
      return json(view(params.company_id as string));
    },
    "operator.entitlements.put": ({ me, params, body }) => {
      const companyId = params.company_id as string;
      if (!operatorOf(me, companyId)) return notFound();
      const b = (body ?? {}) as {
        feature_key?: string;
        status?: EntitlementStatus;
        source?: string;
        starts_on?: string;
        ends_on?: string | null;
        note?: string | null;
      };
      const spec = ADDON_CATALOG.find((a) => a.key === b.feature_key);
      if (!spec || !b.status || !b.source || !b.starts_on)
        return validation("feature_key, status, source and starts_on are required");
      if (b.starts_on > today()) return validation("an entitlement cannot start in the future yet");
      if (b.ends_on) return validation("an entitlement cannot carry an end date yet");
      const enabling = ON.has(b.status);
      if (enabling && !spec.active) return validation(`add-on ${spec.key} is not available yet`);
      const changes: { key: AddonKey; bundledBy: AddonKey | null }[] = [
        { key: spec.key, bundledBy: null },
      ];
      if (enabling) {
        const requires = spec.dependencies.filter(
          (d) => d.mode === "requires" && !ON.has(statusOf(companyId, d.key)),
        );
        if (requires.length)
          return problem(409, "addon_requires", "Add-on requires another add-on");
        for (const d of spec.dependencies)
          if (d.mode === "bundles" && !ON.has(statusOf(companyId, d.key)))
            changes.push({ key: d.key, bundledBy: spec.key });
      } else {
        const requiredBy = ADDON_CATALOG.filter(
          (a) =>
            a.dependencies.some((d) => d.key === spec.key) && ON.has(statusOf(companyId, a.key)),
        );
        if (requiredBy.length)
          return problem(
            409,
            "addon_required_by",
            "Add-on is required by another add-on",
            `${requiredBy.map((a) => a.name).join(", ")} depends on ${spec.name}`,
          );
      }
      const at = deps.now().toISOString();
      for (const ch of changes) {
        setStatus(companyId, ch.key, b.status, {
          id: uid(
            0xe100,
            rowsOf(companyId).length + 1 + (Number.parseInt(companyId.slice(-4), 16) % 4096) * 64,
          ),
          company_id: companyId,
          feature_key: ch.key,
          status: b.status,
          source: ch.bundledBy ? "bundled" : (b.source as LiveRow["source"]),
          bundled_by_feature_key: ch.bundledBy,
          starts_on: b.starts_on,
          ends_on: null,
          grace_until: b.status === "grace" ? addDays(b.starts_on, GRACE_DAYS) : null,
          granted_by_user_id: me.user.id,
          note: ch.bundledBy ? `bundled by ${ch.bundledBy}` : (b.note ?? null),
          created_at: at,
        });
      }
      return json(view(companyId));
    },

    "operator.consumers.retry": ({ me, params, body }) => {
      const b = (body ?? {}) as { company_id?: string; event_id?: string };
      if (!operatorOf(me, b.company_id)) return notFound();
      const r = state.consumers.find(
        (c) =>
          c.consumer_name === params.name &&
          c.company_id === b.company_id &&
          c.event_id === b.event_id,
      );
      if (!r) return notFound();
      if (r.state !== "failed")
        return problem(409, "conflict", "Conflict", `the record is ${r.state}`);
      Object.assign(r, {
        state: "retrying",
        attempts: 0,
        next_attempt_at: deps.now().toISOString(),
      });
      return json(r);
    },
    "operator.consumers.skip": ({ me, params, body }) => {
      const b = (body ?? {}) as { company_id?: string; event_id?: string; reason?: string };
      if (!operatorOf(me, b.company_id)) return notFound();
      if (!b.reason?.trim()) return validation("reason is required");
      const r = state.consumers.find(
        (c) =>
          c.consumer_name === params.name &&
          c.company_id === b.company_id &&
          c.event_id === b.event_id,
      );
      if (!r) return notFound();
      if (r.state !== "failed")
        return problem(409, "conflict", "Conflict", `the record is ${r.state}`);
      Object.assign(r, { state: "skipped_by_operator", next_attempt_at: null });
      return json(r);
    },

    // ADI-02: a launch link. The in-memory build has no panel: the link points at a `.invalid`
    // host and the token is not a real one.
    "operator.aigentpanel.launch": ({ me, body }) => {
      const b = (body ?? {}) as { company_id?: string; de_membership_id?: string };
      if (!me.agency || !operatorOf(me, b.company_id)) return notFound();
      if (
        b.de_membership_id &&
        !deps.desOf(b.company_id as string).some((d) => d.membership_id === b.de_membership_id)
      )
        return notFound();
      const expires = new Date(Date.now() + 60_000).toISOString();
      return json(
        {
          url: `${FAKE_PANEL_ORIGIN}/api/companyos/launch?token=fake.${uid(0x7a00, ++launchSeq)}`,
          expires_at: expires,
        },
        201,
      );
    },

    // SYSTEM-AGENT: the fake companies have no system agent (an honest empty list).
    "operator.systemAgents.list": ({ me }) => {
      if (!me.agency) return problem(403, "forbidden", "Forbidden");
      return json({ data: [] });
    },

    "operator.digitalEmployees.list": ({ me, url }) => {
      if (!me.agency) return problem(403, "forbidden", "Forbidden");
      const week = url.searchParams.get("week") ?? deps.todayWeek();
      const data = me.agency.grants.flatMap((g) => {
        const p = deps.principalFor(me, g.company_id);
        if (!p?.operator) return [];
        const kpi = deps.kpiOn(me, p.company.id);
        return deps.desOf(p.company.id).map((de) => {
          const card = kpi ? deps.cardFor(p, de, week) : null;
          return {
            company: { id: p.company.id, slug: p.company.slug, name: p.company.name },
            digital_employee: deSummary(de, deps.todayWeek(), { operator: true }),
            scorecard: card
              ? {
                  period_type: "week" as const,
                  period_key: week,
                  kpi_score: card.kpi_score,
                  completeness: card.completeness,
                  band: card.band,
                }
              : null,
            spend_alert: false,
            spend_attention: null,
            ...deps.deWeek(de, week),
          };
        });
      });
      return json({ data });
    },

    "costs.list": ({ principal, url }) => {
      if (!principal) return notFound();
      const period = url.searchParams.get("period");
      if (period && !MONTH.test(period)) return validation("period must be a month key");
      const rows = ledgerOf(principal)
        .filter((e) => !period || e.period_key === period)
        .sort((a, b) => b.occurred_at.localeCompare(a.occurred_at) || b.id.localeCompare(a.id));
      const byKind: Record<string, number> = {};
      for (const e of rows) byKind[e.kind] = (byKind[e.kind] ?? 0) + e.amount_minor;
      return json({
        ledger: page(rows, url),
        rollup: {
          by_kind: byKind,
          total_minor: rows.reduce((s, e) => s + e.amount_minor, 0),
          currency: principal.company.reporting_currency,
        },
      });
    },
    "costs.create": ({ principal, body }) => {
      if (!principal) return notFound();
      if (!principal.manager) return problem(403, "forbidden", "Forbidden");
      const b = (body ?? {}) as Partial<CostEvent> & { note?: string };
      if (!b.kind || b.kind === "reversal" || b.kind === "human_time")
        return validation("kind must be a manual kind");
      if (typeof b.amount_minor !== "number" || !Number.isInteger(b.amount_minor))
        return validation("amount_minor must be an integer");
      if (b.amount_minor < 0 && b.kind !== "adjustment")
        return validation("only an adjustment may be negative; corrections are reversals");
      if (b.currency !== principal.company.reporting_currency)
        return validation(`the company's ledger is in ${principal.company.reporting_currency}`);
      if (!b.meter_identity || !METER.test(b.meter_identity))
        return validation("meter identity {system}:{kind}:{external_id}");
      if (RESERVED.some((r) => b.meter_identity?.startsWith(r)))
        return validation("this meter prefix is posted by the platform only");
      if (!b.occurred_at || Date.parse(b.occurred_at) > deps.now().getTime() + 300_000)
        return validation("occurred_at is more than 5 minutes ahead");
      if (b.seat_id && !deps.desOf(principal.company.id).some((d) => d.seat_id === b.seat_id))
        return validation("no such seat");
      const dup = state.costs.find(
        (c) => c.company_id === principal.company.id && c.meter_identity === b.meter_identity,
      );
      if (dup) return json(dup, 200);
      const row = post(principal, {
        occurred_at: new Date(b.occurred_at).toISOString(),
        kind: b.kind,
        amount_minor: b.amount_minor,
        currency: b.currency,
        amount_micros: null,
        seat_id: b.seat_id ?? null,
        holder_membership_id: b.holder_membership_id ?? null,
        meter_identity: b.meter_identity,
        meter: null,
        source_ref: {
          system: "manual",
          kind: "post",
          id: b.meter_identity,
          ...(b.note ? { note: b.note } : {}),
        },
        reverses_cost_event_id: null,
        price_list_ref: null,
        visibility: principal.operator ? (b.visibility ?? "client") : "client",
        actor_kind: principal.operator ? "agency_user" : "person",
        actor_membership_id: null,
      });
      return json(row, 201);
    },
    "costs.reverse": ({ principal, params, body }) => {
      if (!principal) return notFound();
      if (!principal.manager) return problem(403, "forbidden", "Forbidden");
      const reason = (body as { reason?: string } | null)?.reason?.trim();
      if (!reason) return validation("reason is required");
      const original = ledgerOf(principal).find((c) => c.id === params.id);
      if (!original) return notFound();
      if (original.kind === "reversal" || original.meter_identity.startsWith("reversal:"))
        return problem(409, "conflict", "Conflict", "a reversal is not reversed; post a new row");
      if (original.kind === "human_time")
        return problem(409, "conflict", "Conflict", "human-time cost follows its time entry");
      if (original.amount_minor === 0)
        return problem(409, "conflict", "Conflict", "a zero row has nothing to reverse");
      if (reversed(original.id))
        return problem(409, "conflict", "Conflict", "this row is already reversed");
      const credit = original.amount_minor < 0;
      const row = post(principal, {
        occurred_at: original.occurred_at,
        kind: credit ? "adjustment" : "reversal",
        amount_minor: -original.amount_minor,
        currency: original.currency,
        amount_micros: original.amount_micros === null ? null : -original.amount_micros,
        seat_id: original.seat_id,
        holder_membership_id: original.holder_membership_id,
        meter_identity: `reversal:cost_event:${original.id}`,
        meter: original.meter,
        source_ref: { system: "cost_event", kind: "reversal", id: original.id, note: reason },
        reverses_cost_event_id: credit ? null : original.id,
        price_list_ref: null,
        visibility: original.visibility,
        actor_kind: principal.operator ? "agency_user" : "person",
        actor_membership_id: null,
      });
      return json(row, 201);
    },

    "reports.send": (ctx: Ctx) => {
      const { principal, params, body, headers } = ctx;
      if (!principal) return notFound();
      if (!principal.manager) return problem(403, "forbidden", "Forbidden");
      const r = reportById(principal, params.id);
      const force = (body as { force?: boolean } | null)?.force === true;
      const key = headers.get("idempotency-key");
      if (r?.state === "sent" && force && key) {
        // A deliberate resend: one more delivery per recipient, once per key.
        if (!UUID.test(key)) return validation("a forced send's Idempotency-Key must be a UUID");
        const ops = state.resends.get(r.id) ?? [];
        if (!ops.includes(`resend:${key}`)) ops.push(`resend:${key}`);
        state.resends.set(r.id, ops);
        return json(r);
      }
      return deps.sendBase(ctx);
    },
    "reports.reconcile": ({ principal, params, body }) => {
      if (!principal) return notFound();
      if (!principal.operator) return problem(403, "forbidden", "Forbidden");
      const r = reportById(principal, params.id);
      if (!r) return notFound();
      const b = (body ?? {}) as { email?: string; operation?: string; note?: string };
      if (!b.note || b.note.trim().length < 10) return validation("note: at least 10 characters");
      const email = b.email?.trim().toLowerCase() ?? "";
      if (!r.frozen_at || !r.recipients.some((x) => x.email.toLowerCase() === email))
        return problem(
          409,
          "conflict",
          "Conflict",
          "no delivery of this version to that recipient was started",
        );
      const k = `${r.id}|${email}|${b.operation ?? "send"}`;
      // The fake's ledger: the oldest sent version's first recipient is unconfirmed (a broken
      // SMTP session), every other delivery confirmed.
      const oldestSent = deps
        .reportsOf(principal.company)
        .filter((x) => x.state === "sent")
        .sort((a, b2) => a.week_key.localeCompare(b2.week_key))[0];
      const seeded =
        oldestSent?.id === r.id &&
        r.recipients[0]?.email.toLowerCase() === email &&
        !b.operation?.startsWith("resend:")
          ? "unconfirmed"
          : "sent";
      const current = state.deliveries.get(k) ?? seeded;
      if (current !== "unconfirmed")
        return problem(
          409,
          "conflict",
          "Conflict",
          `the delivery is ${current}; only an unconfirmed one is reconciled`,
        );
      state.deliveries.set(
        k,
        (body as { outcome?: string }).outcome === "delivered" ? "sent" : "failed",
      );
      return json(r);
    },
  };
}
